import React, { useState, useEffect, useRef } from 'react';
import { Brain, Send, Loader2, Bot, User, Trash2, Power, PowerOff, Sparkles, AlertTriangle, Target, FileText, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAgent = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [agents, setAgents] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [activeTab, setActiveTab] = useState('chat');
  const [report, setReport] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadAgents();
    loadAnomalies();
    loadPriorities();
    // Message d'accueil
    setMessages([{
      role: 'assistant',
      content: `👋 Bonjour ! Je suis le **Super Agent** de SocialAC.

Je suis une IA ultra-puissante avec un accès **complet** à votre plateforme.

**Ce que je peux FAIRE :**
- 🔄 Changer le statut d'un dossier
- 👤 Assigner un gestionnaire
- 📝 Créer des suivis
- 🔔 Envoyer des notifications
- 📦 Archiver des dossiers

**Ce que je peux ANALYSER :**
- 🔍 Détecter les anomalies
- 📊 Analyser les performances
- ⚠️ Identifier les risques

**Ce que je peux GÉNÉRER :**
- 📧 Rédiger des emails
- 📋 Créer des rapports
- 📌 Lister les priorités

**Essayez :** "Qu'est-ce qui est urgent aujourd'hui ?" ou "Passe le dossier API-2026-0001 en cours"`
    }]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadAgents = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/super-agent/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAgents(data.agents || []);
    } catch (error) {
      console.error('Erreur chargement agents:', error);
    }
  };

  const loadAnomalies = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/super-agent/anomalies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAnomalies(data.anomalies || []);
    } catch (error) {
      console.error('Erreur chargement anomalies:', error);
    }
  };

  const loadPriorities = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/super-agent/priorities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPriorities(data.priorities || []);
    } catch (error) {
      console.error('Erreur chargement priorités:', error);
    }
  };

  const loadDailyReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/super-agent/report/daily`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setReport(data);
    } catch (error) {
      console.error('Erreur chargement rapport:', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/super-agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Erreur');
      }

      setSessionId(data.session_id);
      
      let response = data.response;
      
      // Ajouter les résultats d'actions si présents
      if (data.actions_executed && data.actions_executed.length > 0) {
        response += '\n\n---\n**Actions exécutées :**';
        data.actions_executed.forEach(action => {
          if (action.result?.success) {
            response += `\n✅ ${action.result.message}`;
          } else if (action.result?.error) {
            response += `\n❌ ${action.action}: ${action.result.error}`;
          }
        });
        
        // Recharger les données
        loadAnomalies();
        loadPriorities();
        loadAgents();
      }
      
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
      
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `❌ Erreur: ${error.message}` 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleAgent = async (agentId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/super-agent/agents/${agentId}/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadAgents();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const deleteAgent = async (agentId) => {
    if (!window.confirm('Supprimer cet agent ?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/super-agent/agents/${agentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadAgents();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const clearChat = () => {
    if (!window.confirm('Effacer la conversation ?')) return;
    setMessages([{
      role: 'assistant',
      content: '💬 Nouvelle conversation. Comment puis-je vous aider ?'
    }]);
    setSessionId(null);
  };

  const quickActions = [
    { label: "Qu'est-ce qui est urgent ?", icon: "🚨" },
    { label: "Rapport du jour", icon: "📋" },
    { label: "Anomalies détectées", icon: "⚠️" },
    { label: "Performance de l'équipe", icon: "📊" },
  ];

  return (
    <div className="h-[calc(100vh-120px)] flex gap-4">
      {/* Zone principale */}
      <div className="flex-1 flex flex-col bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  Super Agent
                  <Sparkles className="w-5 h-5 text-yellow-300" />
                </h1>
                <p className="text-purple-100 text-sm">Claude Sonnet 4.5 • Accès complet • Actions directes</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {anomalies.length > 0 && (
                <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium animate-pulse">
                  {anomalies.length} anomalie(s)
                </span>
              )}
              <button
                onClick={clearChat}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition"
                title="Nouvelle conversation"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="px-4 py-3 bg-purple-50 border-b border-purple-100 flex gap-2 overflow-x-auto">
          {quickActions.map((action, idx) => (
            <button
              key={idx}
              onClick={() => {
                setInput(action.label);
                setTimeout(() => handleSend(), 100);
              }}
              className="px-3 py-1.5 bg-white border border-purple-200 rounded-full text-sm text-purple-700 hover:bg-purple-100 transition whitespace-nowrap flex items-center gap-1"
            >
              <span>{action.icon}</span>
              {action.label}
            </button>
          ))}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                msg.role === 'user' 
                  ? 'bg-green-100 text-green-600' 
                  : 'bg-gradient-to-br from-violet-500 to-purple-600 text-white'
              }`}>
                {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
              </div>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}>
                <div className="whitespace-pre-wrap text-sm leading-relaxed" 
                  dangerouslySetInnerHTML={{ 
                    __html: msg.content
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\n/g, '<br/>') 
                  }} 
                />
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-gray-100 rounded-2xl px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                <span className="text-sm text-gray-500">Réflexion en cours...</span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Demandez-moi n'importe quoi... Je peux agir sur vos dossiers !"
              rows="2"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              disabled={loading}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="px-6 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-semibold hover:from-violet-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Panel latéral */}
      <div className="w-80 flex flex-col gap-4">
        {/* Anomalies */}
        {anomalies.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-red-200 overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-orange-500 px-4 py-2">
              <h2 className="text-white font-semibold flex items-center gap-2 text-sm">
                <AlertTriangle className="w-4 h-4" />
                Anomalies ({anomalies.length})
              </h2>
            </div>
            <div className="p-3 space-y-2 max-h-40 overflow-y-auto">
              {anomalies.map((anomaly, idx) => (
                <p key={idx} className="text-xs text-gray-700">{anomaly}</p>
              ))}
            </div>
          </div>
        )}

        {/* Priorités */}
        {priorities.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-amber-200 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-4 py-2">
              <h2 className="text-white font-semibold flex items-center gap-2 text-sm">
                <Target className="w-4 h-4" />
                Priorités ({priorities.length})
              </h2>
            </div>
            <div className="p-3 space-y-2 max-h-40 overflow-y-auto">
              {priorities.slice(0, 5).map((p, idx) => (
                <div key={idx} className="text-xs">
                  <span className="font-medium">{p.type}</span>
                  <p className="text-gray-600">{p.reference} - {p.action}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Agents */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex-1">
          <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-4 py-2">
            <h2 className="text-white font-semibold flex items-center gap-2 text-sm">
              <Bot className="w-4 h-4" />
              Agents IA ({agents.length})
            </h2>
          </div>
          
          <div className="p-3 space-y-2 overflow-y-auto max-h-60">
            {agents.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <Bot className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-xs">Aucun agent</p>
                <p className="text-xs text-gray-400">Demandez-moi d'en créer un !</p>
              </div>
            ) : (
              agents.map(agent => (
                <div 
                  key={agent.id}
                  className={`p-2 rounded-lg border ${
                    agent.enabled 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium truncate ${
                      agent.enabled ? 'text-green-800' : 'text-gray-600'
                    }`}>
                      {agent.name}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleAgent(agent.id)}
                        className={`p-1 rounded ${
                          agent.enabled ? 'text-green-600' : 'text-gray-400'
                        }`}
                      >
                        {agent.enabled ? <Power className="w-3 h-3" /> : <PowerOff className="w-3 h-3" />}
                      </button>
                      <button
                        onClick={() => deleteAgent(agent.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAgent;
